import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Client, ClientsService, ClientsQuery, UserSettingsQuery } from 'timeghost-api';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, filter, startWith, debounceTime, takeUntil, map, finalize, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, combineLatest, Subject, defer } from 'rxjs';
import { CreateClientComponent } from '../create-client/create-client.component';
import { UntypedFormControl } from '@angular/forms';
import { sortBy, flow } from 'lodash-es';
import { hasPermission } from '@app/_helpers/utils';

@Component({
  selector: 'app-client-picker-dialog',
  templateUrl: './client-picker-dialog.component.html',
  styleUrls: ['./client-picker-dialog.component.scss'],
  host: {
    class: 'search-dialog-host',
  },
})
export class ClientPickerDialogComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  searchinput: UntypedFormControl = new UntypedFormControl('');

  readonly workspace$createPermission = this.userSettingsQuery.select().pipe(
    map((user) => {
      return user.workspace.permissionSettings?.groupsCanManageClients?.find((x) => hasPermission(x.id, user));
    })
  );
  private _selectedClient = new BehaviorSubject<Client>(null);
  readonly selectedClient$ = this._selectedClient.asObservable().pipe(distinctUntilChanged());
  get selectedClient() {
    return this._selectedClient.getValue();
  }
  set selectedClient(val: Client) {
    this._selectedClient.next(val);
  }

  private _isLoading = new BehaviorSubject<boolean>(true);
  readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
  get isLoading() {
    return this._isLoading.getValue();
  }
  set isLoading(val: boolean) {
    this._isLoading.next(val);
  }
  defaultClient: Client;
  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: ClientDialogData,
    private ref: MatDialogRef<ClientPickerDialogComponent>,
    private dialog: MatDialog,
    private clientsQuery: ClientsQuery,
    private userSettingsQuery: UserSettingsQuery
  ) {
    this.defaultClient = this.clientsQuery.getAll().find((x) => x.useAsDefault === true);
    this.selectedClient = this.data.selectedClient || this.defaultClient;
  }

  ngOnInit() {
    this.ref.addPanelClass(['client-picker-dialog-container', 'mat-dialog-relative']);
    this.ref.updateSize('400px');
  }

  isSelected(id: string) {
    return this.selectedClient?.id === id;
  }
  data$: Observable<Client[]> = this.clientsQuery.selectAll().pipe(
    takeUntil(this.destroy$),
    map((x) => x.filter((client) => client.name && !client.useAsDefault))
  );
  data$filtered = combineLatest([
    this.searchinput.valueChanges.pipe(startWith(''), debounceTime(150), distinctUntilChanged()),
    this.selectedClient$,
    defer(() => {
      this.isLoading = true;
      return this.data$.pipe(
        tap(() => (this.isLoading = false)),
        startWith()
      );
    }),
  ]).pipe(
    filter(([, , data]) => !!data),
    map(([search, selected, clients]: [string, Client, Client[]]) => {
      const advSearch = search && search.trim().length > 0 ? search.split(' ').filter((s) => s) : null;
      return flow(
        (data: (Client & { selected?: boolean })[]) =>
          selected ? data.map((x) => ({ ...x, selected: x.id === selected.id })) : data,
        (data) =>
          advSearch
            ? data.filter(
                (x) =>
                  advSearch.filter((s) => x.name.toLowerCase().indexOf(s.toLowerCase()) !== -1).length >=
                    advSearch.length || x.selected
              )
            : data
      )(clients);
    })
  );
  selectItem(client: Client) {
    const selectedClient = this.selectedClient;
    if (this.data.canToggle && !!selectedClient) {
      if (selectedClient.id === client.id) {
        this.selectedClient = this.defaultClient;
        if (this.data.closeOnRemove) {
          this.ref.close(this.selectedClient);
        }
        return;
      }
    }
    this.selectedClient = client;
    this.ref.close(client);
  }
  trackFiltered(index: number, item: Client) {
    return item?.id || index;
  }
  createDialog() {
    this.dialog
      .open(CreateClientComponent, {
        data: {
          search: this.searchinput.value,
        },
      })
      .afterClosed()
      .pipe(filter((x) => !!x))
      .subscribe((x: Client) => this.selectItem(x));
  }
}

export interface ClientDialogData {
  selectedClient: Client;
  canToggle: boolean;
  closeOnRemove?: boolean;
}
